/**
 * Survey Distribution Progress Component
 * Shows real-time progress of async survey distribution
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, AlertCircle, Loader } from 'lucide-react';
import { getDistributionStatus } from '../../services/surveyAPI';

const SurveyDistributionProgress = ({ surveyId, jobId, onComplete, onClose }) => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(true);

  useEffect(() => {
    let pollInterval;

    const pollStatus = async () => {
      try {
        const response = await getDistributionStatus(surveyId, jobId);
        // Response from axios is in response.data
        const statusData = response.data || response;
        setStatus(statusData);

        // Stop polling if completed or failed
        if (statusData.status === 'COMPLETED' || statusData.status === 'FAILED') {
          setPolling(false);
          if (statusData.status === 'COMPLETED' && onComplete) {
            setTimeout(() => onComplete(), 2000);
          }
        }
      } catch (err) {
        console.error('Error polling status:', err);
        setError(err.message);
        setPolling(false);
      }
    };

    // Initial poll
    pollStatus();

    // Poll every 2 seconds while in progress
    if (polling) {
      pollInterval = setInterval(pollStatus, 2000);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [surveyId, jobId, polling, onComplete]);

  if (error) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '0.75rem',
          padding: '2rem',
          maxWidth: '500px',
          width: '90%',
          textAlign: 'center'
        }}>
          <AlertCircle size={48} color="#dc2626" style={{ margin: '0 auto 1rem' }} />
          <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
            Error Checking Status
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>{error}</p>
          <button onClick={onClose} style={{
            padding: '0.75rem 1.5rem',
            background: '#6366f1',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer'
          }}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '0.75rem',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <Loader size={48} color="#6366f1" className="animate-spin" style={{ margin: '0 auto 1rem' }} />
          <p style={{ color: '#6b7280' }}>Loading distribution status...</p>
        </div>
      </div>
    );
  }

  const progressPercent = status.progress?.percent_complete || 0;
  const isCompleted = status.status === 'COMPLETED';
  const isFailed = status.status === 'FAILED';
  const isInProgress = status.status === 'IN_PROGRESS' || status.status === 'PENDING';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '0.75rem',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
        maxWidth: '500px',
        width: '90%',
        padding: '2rem'
      }}>
        {/* Status Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: isCompleted ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            : isFailed ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
            : 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem'
        }}>
          {isCompleted && <CheckCircle size={48} color="white" />}
          {isFailed && <AlertCircle size={48} color="white" />}
          {isInProgress && <Clock size={48} color="white" className="animate-pulse" />}
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: '#111827',
          marginBottom: '0.5rem',
          textAlign: 'center'
        }}>
          {isCompleted && 'Distribution Complete!'}
          {isFailed && 'Distribution Failed'}
          {isInProgress && 'Distributing Surveys...'}
        </h2>

        {/* Progress Bar */}
        {isInProgress && (
          <div style={{
            width: '100%',
            height: '8px',
            background: '#e5e7eb',
            borderRadius: '9999px',
            overflow: 'hidden',
            marginBottom: '1rem'
          }}>
            <div style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        )}

        {/* Progress Stats */}
        <div style={{
          padding: '1rem',
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem' }}>
            <div>
              <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>Participants Processed</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827' }}>
                {status.progress?.processed_participants || 0} / {status.progress?.total_participants || 0}
              </div>
            </div>
            <div>
              <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>Total Recipients</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#6366f1' }}>
                {status.progress?.created_instances || 0}
                {status.progress?.total_instances && ` / ${status.progress.total_instances}`}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                (Self, Manager, Direct Reports, Ad-hoc)
              </div>
            </div>
          </div>

          {status.progress?.failed_instances > 0 && (
            <div style={{
              marginTop: '0.75rem',
              padding: '0.5rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '0.375rem',
              color: '#dc2626',
              fontSize: '0.875rem'
            }}>
              {status.progress.failed_instances} recipient(s) failed
            </div>
          )}

          {isInProgress && (
            <div style={{
              marginTop: '0.75rem',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '0.875rem'
            }}>
              {progressPercent}% complete
            </div>
          )}
        </div>

        {/* Error Details */}
        {isFailed && status.error_details && (
          <div style={{
            padding: '1rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#dc2626', marginBottom: '0.5rem' }}>
              Error Details:
            </div>
            <div style={{ fontSize: '0.875rem', color: '#991b1b' }}>
              {status.error_details.message}
            </div>
          </div>
        )}

        {/* Action Button */}
        {(isCompleted || isFailed) && (
          <button onClick={onClose} style={{
            width: '100%',
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}>
            Close
          </button>
        )}

        {isInProgress && (
          <div style={{
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '0.875rem'
          }}>
            Please wait while we distribute the surveys...
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyDistributionProgress;
